import React from 'react';
import { Star, Lock, ShieldCheck, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { isVenueClaimed } from './venueHelpers';

const ReviewCard = ({ review, blurred }) => (
  <div className={`bg-gradient-to-br from-gray-50 to-white p-5 rounded-2xl border border-gray-100 ${blurred ? 'select-none pointer-events-none' : 'hover:shadow-md transition-all duration-300 group'}`}>
    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-3">
      <div className="flex items-start gap-3">
        <div className="w-12 h-12 bg-gradient-to-br from-pink-400 to-purple-600 rounded-full flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
          <span className="text-white font-bold text-lg">{(review.authorName || review.author)?.charAt(0) || 'M'}</span>
        </div>
        <div>
          <p className="font-bold text-gray-900">{review.authorName || review.author || 'Misafir'}</p>
          <div className="flex items-center gap-1 mt-1">
            {[...Array(5)].map((_, i) => (
              <Star key={i} className={`w-4 h-4 ${i < review.rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`} />
            ))}
          </div>
        </div>
      </div>
      <span className="text-xs text-gray-500 bg-gray-100 px-3 py-1 rounded-full">{review.relativeTime || ''}</span>
    </div>
    <p className="text-sm text-gray-700 leading-relaxed break-words">{review.text}</p>
  </div>
);

const UnclaimedTeaser = ({ venue }) => {
  const navigate = useNavigate();
  const total = venue.googleReviewCount || venue.reviewCount || venue.googleReviews.length;
  const rating = venue.googleRating || venue.rating;
  const preview = venue.googleReviews.filter(r => r?.text).slice(0, 2);
  if (preview.length === 0) return null;

  return (
    <div id="reviews-section" className="bg-white rounded-3xl shadow-lg border border-gray-100 overflow-hidden scroll-mt-24" data-testid="venue-reviews-teaser">
      <div className="p-6 sm:p-8">
        <div className="flex items-center gap-3 mb-6">
          <div className="bg-gradient-to-r from-yellow-500 to-orange-600 p-3 rounded-2xl shadow-lg">
            <Star className="w-6 h-6 text-white fill-white" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Google Yorumları</h2>
            <p className="text-sm text-gray-500">{rating ? `⭐ ${Number(rating).toFixed(1)} · ` : ''}{total} Google yorumu</p>
          </div>
        </div>

        <div className="relative">
          <div className="space-y-4 blur-[6px] opacity-70" aria-hidden="true">
            {preview.map((review, i) => <ReviewCard key={i} review={review} blurred />)}
          </div>
          <div className="absolute inset-0 bg-gradient-to-b from-white/30 via-white/70 to-white flex items-center justify-center p-4">
            <div className="max-w-md w-full text-center bg-white/95 backdrop-blur rounded-2xl border border-pink-100 shadow-xl p-6">
              <div className="mx-auto w-12 h-12 rounded-full bg-pink-50 text-pink-600 flex items-center justify-center mb-3">
                <Lock className="w-5 h-5" />
              </div>
              <p className="font-bold text-gray-900 text-lg leading-snug" data-testid="venue-reviews-teaser-title">
                {total} Google yorumu — mekan sahibi doğrulayınca görünür
              </p>
              <p className="text-sm text-gray-600 mt-2">
                Bu mekan henüz sahiplenilmedi. İşletme sahibiyseniz 2 dakikada doğrulayın, 15 gün ücretsiz Pro paketle yorumlarınız, telefon ve adres bilginiz ziyaretçilere açılsın.
              </p>
              <button
                type="button"
                onClick={() => navigate(`/isortagi-basvuru/${venue.slug}`)}
                className="mt-4 inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-pink-600 to-purple-600 text-white font-semibold text-sm px-5 py-2.5 shadow-md hover:shadow-lg hover:-translate-y-0.5 transition-[transform,box-shadow]"
                data-testid="venue-reviews-teaser-claim-btn"
              >
                <ShieldCheck className="w-4 h-4" /> Sahiplen · 15 Gün Ücretsiz <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const VenueReviews = React.memo(({ venue }) => {
  if (!venue.googleReviews || venue.googleReviews.length === 0 || venue.hideGoogleReviews) return null;
  if (!isVenueClaimed(venue)) return <UnclaimedTeaser venue={venue} />;

  return (
    <div id="reviews-section" className="bg-white rounded-3xl shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100 overflow-hidden scroll-mt-24">
      <div className="p-6 sm:p-8">
        <div className="flex items-center gap-3 mb-8">
          <div className="bg-gradient-to-r from-yellow-500 to-orange-600 p-3 rounded-2xl shadow-lg">
            <Star className="w-6 h-6 text-white fill-white" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Google Yorumları</h2>
            <p className="text-sm text-gray-500">{venue.googleReviews.length} Google yorumu</p>
          </div>
        </div>
        <div className="space-y-4">
          {venue.googleReviews.slice(0, 5).map((review, index) => <ReviewCard key={index} review={review} />)}
        </div>
      </div>
    </div>
  );
});

VenueReviews.displayName = 'VenueReviews';
export default VenueReviews;
